import {
	Autocomplete,
	Box,
	Button,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import React, { useEffect, useState } from 'react';
import ParkingLocationSelector from './ParkingLocationSelector';
import LotHistoryDrawer from '../lotHistoryDrawer/LotHistoryDrawer';
import { ParkingLocation } from '../../types/parkingLocation';

interface Customer {
	Id: string;
	Name: string;
}

interface ContextBarProps {
	customers: Customer[];
	loadingCustomers: boolean;
	selectedCustomer: Customer | null;
	setSelectedCustomer: (c: Customer | null) => void;
	activeParkingLocation: ParkingLocation | null;
	setActiveParkingLocation: React.Dispatch<
		React.SetStateAction<ParkingLocation | null>
	>;
}

const ContextBar: React.FC<ContextBarProps> = ({
	customers,
	loadingCustomers,
	selectedCustomer,
	setSelectedCustomer,
	activeParkingLocation,
	setActiveParkingLocation,
}) => {
	const [userExpanded, setUserExpanded] = useState(false);
	const [openLogModal, setOpenLogModal] = useState(false);
	const isComplete = !!selectedCustomer && !!activeParkingLocation;

	const lotHistoryButton = activeParkingLocation && (
		<Button
			startIcon={<MoreHorizIcon />}
			size="small"
			variant="outlined"
			sx={{ borderRadius: 999, alignSelf: 'flex-start' }}
			onClick={() => setOpenLogModal(true)}
		>
			Lot History
		</Button>
	);

	const lotHistoryModal = activeParkingLocation && (
		<LotHistoryDrawer
			open={openLogModal}
			onClose={() => setOpenLogModal(false)}
			parkingLocation={activeParkingLocation}
		/>
	);
	// Force the selectors visible whenever something is missing — operators
	// can't proceed without both, so hiding them would be a dead end.
	const expanded = userExpanded || !isComplete;

	// When both selections are filled (autofill on mount, or after the user
	// finishes picking) collapse back to the breadcrumb.
	useEffect(() => {
		if (isComplete) setUserExpanded(false);
	}, [isComplete]);

	if (!expanded) {
		return (
			// Keep the tour anchor present in the collapsed state too — the lot is
			// usually preselected, so the bar starts collapsed and the tour's first
			// step would otherwise lose its target and auto-close. The breadcrumb
			// expands on click, letting the user change the customer/lot.
			<Stack spacing={1} data-tour="ticket-select-customer">
				<Box
					onClick={() => setUserExpanded(true)}
					role="button"
					tabIndex={0}
					aria-label="Change customer or parking lot"
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setUserExpanded(true);
						}
					}}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 1,
						px: 1.5,
						py: 1,
						borderRadius: 2,
						bgcolor: 'action.hover',
						cursor: 'pointer',
						transition: 'background-color 120ms ease',
						'&:hover': { bgcolor: 'action.selected' },
						'&:focus-visible': {
							outline: '2px solid',
							outlineColor: 'primary.main',
							outlineOffset: 2,
						},
					}}
				>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							component="div"
							noWrap
						>
							{selectedCustomer?.Name}
						</Typography>
						<Typography variant="body2" fontWeight={500} noWrap>
							{activeParkingLocation?.name}
						</Typography>
					</Box>
					<IconButton
						size="small"
						aria-hidden
						tabIndex={-1}
						sx={{ pointerEvents: 'none' }}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				</Box>
				{lotHistoryButton}
				{lotHistoryModal}
			</Stack>
		);
	}

	return (
		<Stack spacing={1.25} data-tour="ticket-select-customer">
			<Autocomplete
				options={customers || []}
				value={selectedCustomer}
				getOptionLabel={(o) => o.Name}
				// Match by Id, not object identity/name: customer names aren't
				// unique, so keying/comparing by Name gives duplicate-key warnings
				// and "value not in options" when the selected object isn't the
				// same reference as the loaded option.
				isOptionEqualToValue={(option, value) => option.Id === value.Id}
				renderOption={(props, option) => {
					const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & {
						key?: React.Key;
					};
					return (
						<li key={option.Id} {...rest}>
							{option.Name}
						</li>
					);
				}}
				onChange={(_, newValue) => setSelectedCustomer(newValue)}
				renderInput={(params) => (
					<TextField {...params} label="Select Customer" />
				)}
				loading={loadingCustomers}
				loadingText="Loading..."
				clearIcon={false}
				fullWidth
			/>
			<ParkingLocationSelector
				activeParkingLocation={activeParkingLocation}
				setActiveParkingLocation={setActiveParkingLocation}
				selectedCustomerId={selectedCustomer?.Id || null}
				isCameraOn={false}
			/>
			{lotHistoryButton}
			{isComplete && (
				<Box display="flex" justifyContent="flex-end">
					<Button
						size="small"
						onClick={() => setUserExpanded(false)}
					>
						Done
					</Button>
				</Box>
			)}
			{lotHistoryModal}
		</Stack>
	);
};

export default ContextBar;
